import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Header } from "../components/Header";
import { getUser } from "../utils/api";
import { 
  Upload, Calendar, MapPin, User, CreditCard, FileText, 
  Building2, CheckCircle, Home, ChevronRight, AlertCircle,
  Image, X, Eye
} from "lucide-react";

interface FormState {
  establish_date: string;
  register_address: string;
  authorized_representative_name: string;
  authorized_representative_id_card: string;
  authorized_representative_id_card_front: string;
  authorized_representative_id_card_back: string;
  legal_person_name: string;
  legal_person_id_card: string;
  legal_person_id_card_front: string;
  legal_person_id_card_back: string;
  business_license: string;
  authorization_letter: string;
}

interface FileNames {
  [key: string]: string;
}

// SectionCard 组件必须定义在外部，否则每次渲染会创建新组件导致 input 失焦
const SectionCard = ({ title, icon: Icon, children }: { title: string; icon: any; children: React.ReactNode }) => (
  <div style={{
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    overflow: 'hidden',
    marginBottom: '24px'
  }}>
    <div style={{
      padding: '20px 28px',
      borderBottom: '1px solid #f3f4f6',
      background: 'linear-gradient(to right, #fafbfc, white)',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        backgroundColor: '#eff6ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Icon style={{ width: '20px', height: '20px', color: '#003c8c' }} />
      </div>
      <h3 style={{ fontSize: '17px', fontWeight: 600, color: '#1f2937' }}>{title}</h3>
    </div>
    <div style={{ padding: '28px' }}>
      {children}
    </div>
  </div>
);

// FileUploadField 组件必须定义在外部，否则每次渲染会创建新组件导致 input 失焦
interface FileUploadFieldProps {
  field: keyof FormState;
  label: string;
  required?: boolean;
  fileName: string;
  fileUrl: string;
  isUploading: boolean;
  error?: string;
  onFileSelect: (field: string, file: File) => void;
  sampleUrl?: string;
  downloadUrl?: string;
  downloadName?: string;
  onViewSample?: (url: string) => void;
}

const FileUploadField = ({ field, label, required, fileName, fileUrl, isUploading, error, onFileSelect, sampleUrl, downloadUrl, downloadName, onViewSample }: FileUploadFieldProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const displayName = fileName || (fileUrl ? fileUrl.split('/').pop() || '已上传' : '');
  const isImage = displayName && /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(displayName);
  
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
            {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
          </label>
          {(sampleUrl || downloadUrl) && (
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              {sampleUrl && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewSample?.(sampleUrl);
                  }}
                  style={{ color: '#003c8c', cursor: 'pointer', fontSize: '13px', border: 'none', background: 'none', padding: 0 }}
                >
                  查看样例
                </button>
              )}
              {downloadUrl && (
                <a 
                  href={downloadUrl} 
                  download={downloadName || true}
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: '#003c8c', fontSize: '13px', textDecoration: 'none' }}
                >
                  下载授权委托书
                </a>
              )}
            </div>
          )}
        </div>
        {error && <span style={{ fontSize: '12px', color: '#ef4444' }}>{error}</span>}
      </div>
      <div
        onClick={() => !isUploading && fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${error ? '#ef4444' : fileUrl ? '#10b981' : '#e5e7eb'}`,
          borderRadius: '12px',
          height: '160px',
          padding: '0 24px',
          cursor: isUploading ? 'wait' : 'pointer',
          transition: 'all 0.2s',
          backgroundColor: fileUrl ? '#f0fdf4' : '#fafafa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFileSelect(field, file);
          }}
          style={{ display: 'none' }}
          disabled={isUploading}
        />
        {isUploading ? (
          <div style={{ color: '#6b7280', textAlign: 'center' }}>
            <div style={{ 
              width: '32px', 
              height: '32px', 
              border: '3px solid #e5e7eb',
              borderTop: '3px solid #003c8c',
              borderRadius: '50%',
              margin: '0 auto 12px',
              animation: 'spin 1s linear infinite'
            }} />
            上传中...
          </div>
        ) : fileUrl ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <div style={{ 
                  width: '32px', 
                  height: '32px', 
                  borderRadius: '8px', 
                  backgroundColor: '#10b981', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center'
                }}>
                  <CheckCircle style={{ width: '18px', height: '18px', color: 'white' }} />
                </div>
                <div style={{ fontSize: '15px', color: '#10b981', fontWeight: 600 }}>
                  上传成功
                </div>
              </div>
              <div style={{ fontSize: '13px', color: '#374151', fontWeight: 500, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayName}
              </div>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>点击重新上传</div>
            </div>
            
            {isImage && fileUrl && (
              <div style={{ 
                width: '180px', 
                height: '110px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                overflow: 'hidden'
              }}>
                <img
                  src={fileUrl.startsWith('http') ? fileUrl : fileUrl.startsWith('/') ? fileUrl : `/api${fileUrl}`}
                  alt={label}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              width: '56px', 
              height: '56px', 
              borderRadius: '14px', 
              backgroundColor: '#f3f4f6', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <Upload style={{ width: '28px', height: '28px', color: '#9ca3af' }} />
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
              点击上传 <span style={{ fontWeight: 500 }}>{label}</span>
            </div>
            <div style={{ fontSize: '12px', color: '#9ca3af' }}>
              支持 JPG、PNG、PDF 格式
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function CompleteInfoPage() {
  const navigate = useNavigate();
  const [fs, setFs] = useState<FormState>({
    establish_date: '',
    register_address: '',
    authorized_representative_name: '',
    authorized_representative_id_card: '',
    authorized_representative_id_card_front: '',
    authorized_representative_id_card_back: '',
    legal_person_name: '',
    legal_person_id_card: '',
    legal_person_id_card_front: '',
    legal_person_id_card_back: '',
    business_license: '',
    authorization_letter: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [fileNames, setFileNames] = useState<FileNames>({});
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  useEffect(() => {
    const user = getUser();
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.type !== '预注册') {
      navigate('/');
      return;
    }
    
    // 预填充用户已填写的信息
    setFs(prev => ({
      ...prev,
      establish_date: user.establish_date ? user.establish_date.split('T')[0] : '',
      register_address: user.register_address || '',
      authorized_representative_name: user.authorized_representative_name || '',
      authorized_representative_id_card: user.authorized_representative_id_card || '',
      authorized_representative_id_card_front: user.authorized_representative_id_card_front || '',
      authorized_representative_id_card_back: user.authorized_representative_id_card_back || '',
      legal_person_name: user.legal_person_name || '',
      legal_person_id_card: user.legal_person_id_card || '',
      legal_person_id_card_front: user.legal_person_id_card_front || '',
      legal_person_id_card_back: user.legal_person_id_card_back || '',
      business_license: user.business_license || '',
      authorization_letter: user.authorization_letter || '',
    }));
  }, [navigate]);

  const handleFileUpload = async (field: string, file: File) => {
    setUploading(prev => ({ ...prev, [field]: true }));
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const resp = await fetch('/api/upload', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      }).then(r => r.json());

      if (resp?.ok && resp.url) {
        setFileNames(prev => ({ ...prev, [field]: file.name }));
        setFs(prev => ({ ...prev, [field]: resp.url }));
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      } else {
        setErrors(prev => ({ ...prev, [field]: '上传失败，请重试' }));
      }
    } catch (err) {
      setErrors(prev => ({ ...prev, [field]: '上传失败，请重试' }));
    } finally {
      setUploading(prev => ({ ...prev, [field]: false }));
    }
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!fs.establish_date) e.establish_date = '请选择成立时间';
    if (!fs.register_address.trim()) e.register_address = '请输入注册地址';
    if (!fs.authorized_representative_name.trim()) e.authorized_representative_name = '请输入授权代表姓名';
    if (!fs.authorized_representative_id_card.trim()) e.authorized_representative_id_card = '请输入授权代表身份证号';
    
    const idCardReg = /^[1-9]\d{5}(18|19|20)\d{2}((0[1-9])|(1[0-2]))(([0-2][1-9])|10|20|30|31)\d{3}[0-9Xx]$/;
    if (fs.authorized_representative_id_card && !idCardReg.test(fs.authorized_representative_id_card)) {
      e.authorized_representative_id_card = '身份证号格式不正确';
    }
    if (!fs.legal_person_name.trim()) e.legal_person_name = '请输入法人姓名';
    if (!fs.legal_person_id_card.trim()) e.legal_person_id_card = '请输入法人身份证号';
    if (fs.legal_person_id_card && !idCardReg.test(fs.legal_person_id_card)) {
      e.legal_person_id_card = '身份证号格式不正确';
    }
    if (!fs.business_license?.trim()) e.business_license = '请上传营业执照';
    if (!fs.authorization_letter?.trim()) e.authorization_letter = '请上传授权委托书';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch('/api/user/complete-info', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(fs),
      }).then(r => r.json());

      if (resp?.ok) {
        // 清除登录状态，用户需要等待审核通过后重新登录
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setShowSuccessModal(true);
      } else {
        setErrors({ submit: resp?.error || '提交失败，请稍后重试' });
      }
    } catch (err) {
      setErrors({ submit: '提交失败，请稍后重试' });
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (field: string, hasError: boolean) => ({
    width: '100%',
    height: '48px',
    padding: '0 16px 0 48px',
    border: `2px solid ${hasError ? '#ef4444' : focusedField === field ? '#003c8c' : '#e5e7eb'}`,
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s',
    backgroundColor: 'white'
  });

  // 渲染文件上传字段的辅助函数
  const renderFileUploadField = (field: keyof FormState, label: string, required?: boolean, sampleUrl?: string, downloadUrl?: string, downloadName?: string) => (
    <FileUploadField
      field={field}
      label={label}
      required={required}
      fileName={fileNames[field] || ''}
      fileUrl={fs[field]}
      isUploading={uploading[field] || false}
      error={errors[field]}
      onFileSelect={handleFileUpload}
      sampleUrl={sampleUrl}
      downloadUrl={downloadUrl}
      downloadName={downloadName}
      onViewSample={(url) => setPreviewImage(url)}
    />
  );

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Header simple />
      
      {/* 页面横幅 */}
      <div style={{
        background: 'linear-gradient(135deg, #003c8c 0%, #0050b8 50%, #0066cc 100%)',
        padding: '48px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-60px',
          right: '-40px',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
        }} />
        
        <div style={{ width: '70%', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
            <div style={{
              width: '52px',
              height: '52px',
              borderRadius: '14px',
              backgroundColor: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FileText style={{ width: '26px', height: '26px', color: 'white' }} />
            </div>
            <h1 style={{ fontSize: '32px', fontWeight: 700, color: 'white' }}>完善企业信息</h1>
          </div>
          <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.8)' }}>
            请填写企业基本信息和上传相关证件，完成后等待管理员审核
          </p>
        </div>
      </div>

      {/* 面包屑导航 */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ width: '70%', margin: '0 auto', padding: '16px 0' }}>
          <nav style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', textDecoration: 'none' }}>
              <Home style={{ width: '16px', height: '16px' }} />
              首页
            </Link>
            <ChevronRight style={{ width: '16px', height: '16px', color: '#d1d5db' }} />
            <span style={{ color: '#003c8c', fontWeight: 500 }}>完善企业信息</span>
          </nav>
        </div>
      </div>

      {/* 主内容 */}
      <div style={{ width: '70%', margin: '0 auto', padding: '32px 0' }}>
        {/* 基本信息 */}
        <SectionCard title="基本信息" icon={Building2}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>成立时间 <span style={{ color: '#ef4444' }}>*</span></label>
                {errors.establish_date && <span style={{ fontSize: '12px', color: '#ef4444' }}>{errors.establish_date}</span>}
              </div>
              <div style={{ position: 'relative' }}>
                <Calendar style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'establish_date' ? '#003c8c' : '#9ca3af' }} />
                <input
                  type="date"
                  value={fs.establish_date}
                  onChange={(e) => setFs(prev => ({ ...prev, establish_date: e.target.value }))}
                  onFocus={() => setFocusedField('establish_date')}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle('establish_date', !!errors.establish_date)}
                />
              </div>
            </div>
            
            <div style={{ gridColumn: 'span 2' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>注册地址 <span style={{ color: '#ef4444' }}>*</span></label>
                {errors.register_address && <span style={{ fontSize: '12px', color: '#ef4444' }}>{errors.register_address}</span>}
              </div>
              <div style={{ position: 'relative' }}>
                <MapPin style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'register_address' ? '#003c8c' : '#9ca3af' }} />
                <input
                  value={fs.register_address}
                  onChange={(e) => setFs(prev => ({ ...prev, register_address: e.target.value }))}
                  onFocus={() => setFocusedField('register_address')}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle('register_address', !!errors.register_address)}
                  placeholder="请输入注册地址"
                />
              </div>
            </div>
          </div>
        </SectionCard>

        {/* 授权代表信息 */}
        <SectionCard title="授权代表信息" icon={User}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>授权代表姓名 <span style={{ color: '#ef4444' }}>*</span></label>
                {errors.authorized_representative_name && <span style={{ fontSize: '12px', color: '#ef4444' }}>{errors.authorized_representative_name}</span>}
              </div>
              <div style={{ position: 'relative' }}>
                <User style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'authorized_representative_name' ? '#003c8c' : '#9ca3af' }} />
                <input
                  value={fs.authorized_representative_name}
                  onChange={(e) => setFs(prev => ({ ...prev, authorized_representative_name: e.target.value }))}
                  onFocus={() => setFocusedField('authorized_representative_name')}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle('authorized_representative_name', !!errors.authorized_representative_name)}
                  placeholder="请输入授权代表姓名"
                />
              </div>
            </div>
            
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>授权代表身份证号 <span style={{ color: '#ef4444' }}>*</span></label>
                {errors.authorized_representative_id_card && <span style={{ fontSize: '12px', color: '#ef4444' }}>{errors.authorized_representative_id_card}</span>}
              </div>
              <div style={{ position: 'relative' }}>
                <CreditCard style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'authorized_representative_id_card' ? '#003c8c' : '#9ca3af' }} />
                <input
                  value={fs.authorized_representative_id_card}
                  onChange={(e) => setFs(prev => ({ ...prev, authorized_representative_id_card: e.target.value }))}
                  onFocus={() => setFocusedField('authorized_representative_id_card')}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle('authorized_representative_id_card', !!errors.authorized_representative_id_card)}
                  placeholder="请输入18位身份证号"
                />
              </div>
            </div>
            
            {renderFileUploadField("authorized_representative_id_card_front", "授权代表身份证正面", true, "/upload/example/idcard_front.png")}
            {renderFileUploadField("authorized_representative_id_card_back", "授权代表身份证背面", true, "/upload/example/idcard_back.png")}
          </div>
        </SectionCard>

        {/* 法人信息 */}
        <SectionCard title="法人信息" icon={User}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>法人姓名 <span style={{ color: '#ef4444' }}>*</span></label>
                {errors.legal_person_name && <span style={{ fontSize: '12px', color: '#ef4444' }}>{errors.legal_person_name}</span>}
              </div>
              <div style={{ position: 'relative' }}>
                <User style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'legal_person_name' ? '#003c8c' : '#9ca3af' }} />
                <input
                  value={fs.legal_person_name}
                  onChange={(e) => setFs(prev => ({ ...prev, legal_person_name: e.target.value }))}
                  onFocus={() => setFocusedField('legal_person_name')}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle('legal_person_name', !!errors.legal_person_name)}
                  placeholder="请输入法人姓名"
                />
              </div>
            </div>
            
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>法人身份证号 <span style={{ color: '#ef4444' }}>*</span></label>
                {errors.legal_person_id_card && <span style={{ fontSize: '12px', color: '#ef4444' }}>{errors.legal_person_id_card}</span>}
              </div>
              <div style={{ position: 'relative' }}>
                <CreditCard style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'legal_person_id_card' ? '#003c8c' : '#9ca3af' }} />
                <input
                  value={fs.legal_person_id_card}
                  onChange={(e) => setFs(prev => ({ ...prev, legal_person_id_card: e.target.value }))}
                  onFocus={() => setFocusedField('legal_person_id_card')}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle('legal_person_id_card', !!errors.legal_person_id_card)}
                  placeholder="请输入18位身份证号"
                />
              </div>
            </div>
            
            {renderFileUploadField("legal_person_id_card_front", "法人身份证正面", true, "/upload/example/idcard_front.png")}
            {renderFileUploadField("legal_person_id_card_back", "法人身份证背面", true, "/upload/example/idcard_back.png")}
          </div>
        </SectionCard>

        {/* 资质文件 */}
        <SectionCard title="资质文件" icon={FileText}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
            {renderFileUploadField("business_license", "营业执照", true, "/upload/example/account.png")}
            {renderFileUploadField("authorization_letter", "授权委托书", true, "/upload/example/shouquan.png", "/upload/templates/authorization.docx", "授权委托书.docx")}
          </div>
        </SectionCard>

        {/* 错误提示 */}
        {errors.submit && (
          <div style={{
            padding: '16px 20px',
            backgroundColor: '#fef2f2',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '24px'
          }}>
            <AlertCircle style={{ width: '20px', height: '20px', color: '#ef4444' }} />
            <span style={{ color: '#ef4444', fontSize: '14px' }}>{errors.submit}</span>
          </div>
        )}

        {/* 提交按钮 */}
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              padding: '16px 64px',
              backgroundColor: loading ? '#9ca3af' : '#003c8c',
              color: 'white',
              fontSize: '16px',
              fontWeight: 600,
              border: 'none',
              borderRadius: '12px',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 4px 15px rgba(0,60,140,0.3)'
            }}
          >
            {loading ? '提交中...' : '提交审核'}
          </button>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '16px 48px',
              backgroundColor: 'white',
              color: '#003c8c',
              fontSize: '16px',
              fontWeight: 600,
              border: '2px solid #003c8c',
              borderRadius: '12px',
              cursor: 'pointer'
            }}
          >
            取消
          </button>
        </div>
      </div>

      {/* 成功弹窗 */}
      {showSuccessModal && (
        <>
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            zIndex: 50
          }} />
          <div style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: '16px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '20px',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              maxWidth: '400px',
              width: '100%',
              padding: '40px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 24px',
                backgroundColor: '#ecfdf5',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CheckCircle style={{ width: '40px', height: '40px', color: '#10b981' }} />
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#1f2937', marginBottom: '12px' }}>
                提交成功
              </h3>
              <p style={{ color: '#6b7280', marginBottom: '32px', lineHeight: 1.6 }}>
                您的信息已提交，请等待管理员审核。审核结果将通过短信通知您，届时请重新登录系统。
              </p>
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/');
                }}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: '#003c8c',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer'
                }}
              >
                返回首页
              </button>
            </div>
          </div>
        </>
      )}

      {/* 图片预览弹窗 */}
      {previewImage && (
        <div 
          onClick={() => setPreviewImage(null)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out'
          }}
        >
          <img 
            src={previewImage} 
            alt="预览" 
            style={{ 
              maxWidth: '90vw', 
              maxHeight: '90vh', 
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
              cursor: 'default'
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setPreviewImage(null)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
              backdropFilter: 'blur(4px)'
            }}
          >
            <X style={{ width: '24px', height: '24px' }} />
          </button>
        </div>
      )}

      {/* 加载动画样式 */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
